import { Injectable } from '@angular/core';
import { API as AWS_API } from 'aws-amplify';
import { firstValueFrom } from 'rxjs';
import { API, CONST_AWS_API } from 'src/app/core/constants';
import { environment } from 'src/environments/environment';
import { AppPaymentTVService } from './app-payment-tv.service';
import { AppService } from './app.service';
import { CommanSubscribeService } from './comman-subscribe.service';
import { StorageService } from './storage.service';
import { attendeeFromUser, buildQuickRegisterPayload, pickQuickRegisterTicket, questionnaireOn, safeParse } from './event-quick-register.payload';

export { buildQuickRegisterPayload, pickQuickRegisterTicket } from './event-quick-register.payload';

export type QuickRegisterResult = { status: 'registered'; ticketKey: string } | { status: 'needs-form' };

// One-click free registration for a logged-in fan only ("Remind Me", specs/ai-agent-home-redesign
// US-20/US-21); guests never reach this service. Runs the tickets page flow in the background with
// the fan as the only attendee: load the event and its tickets, pick one free ticket (quantity 1),
// fill the attendee from the profile, look the fan up with getUserByEmail, then registerEvent with
// the same payload as EventTicketsComponent.registerEvent(). Returns 'needs-form' when only the fan
// can supply what is missing (no free ticket, shipping on every free ticket, mandatory questions).
@Injectable({ providedIn: 'root' })
export class EventQuickRegisterService {
  constructor(
    private appPaymentService: AppPaymentTVService,
    private appService: AppService,
    private commonSubscribeService: CommanSubscribeService,
    private storageService: StorageService,
  ) {}

  async register(slug: string, user: any, networkGroupId?: number): Promise<QuickRegisterResult> {
    if (!user?.id) throw new Error('Please log in to register');
    const data = await this.getEventBySlug(slug, user);
    const stream = data?.stream_info;
    if (!stream?.id) throw new Error('Event not found');
    // Same as the tickets page: tickets come with the event and questions only apply when the
    // event's questionnaire setting is on.
    const tickets = Array.isArray(data.tickets) ? data.tickets : await this.getTickets(stream.id, user);
    const questions = questionnaireOn(stream) ? await this.getQuestions(stream.id, user) : [];
    const ticket = pickQuickRegisterTicket(tickets, questions);
    if (!ticket) return { status: 'needs-form' };

    const fan = await this.lookUpFan(stream, user);
    // Already holds this ticket: nothing to register, the reload shows the current state.
    if ((fan.ticketIDs || []).includes(ticket.id)) return { status: 'registered', ticketKey: '' };

    const paymentInfo = buildQuickRegisterPayload(stream, ticket, user, fan.user?.id || user.id, networkGroupId, this.countryName());
    if (questions.length > 0) paymentInfo.questions = [];
    const refCode = this.affiliateRefCode();
    if (refCode) paymentInfo.ref_code = refCode;
    const result: any = await firstValueFrom(this.appPaymentService.postRequest(API.REGISTER_EVENT + '?NOLOADER', { paymentInfo }));
    if (result?.status != 0) throw new Error(result?.message || 'Could not register, please try again');
    return { status: 'registered', ticketKey: result.ticket_key || '' };
  }

  // Same request as EventTicketsComponent.checkOtherUsers() for the buyer's own row.
  private async lookUpFan(stream: any, user: any): Promise<any> {
    const attendee = attendeeFromUser(user);
    const request$ = this.appService.postRequest(API.GET_USER_BY_EMAIL, {
      firstname: attendee.first_name,
      lastname: attendee.last_name,
      email: attendee.email,
      stream_id: stream.id,
      performer_id: stream.performer_id,
      organization_id: stream.organization_id,
    });
    if (!request$) throw new Error('Please check network connection');
    const result: any = await firstValueFrom(request$);
    if (result?.status != 0) throw new Error(result?.message || 'Could not register, please try again');
    return result;
  }

  private async getEventBySlug(slug: string, user: any): Promise<any> {
    const response: any = await AWS_API.post(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_EVENT_BY_SLUG, this.request(user, {
      slug,
      userid: String(user.id),
      offset: new Date().getTimezoneOffset(),
      currency: 'USD',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));
    return response?.data?.Data;
  }

  private async getTickets(streamId: any, user: any): Promise<any[]> {
    const url = CONST_AWS_API.GET_TICKETS_BY_EVENT_ID + '/' + streamId + '/' + new Date().getTimezoneOffset();
    const response: any = await AWS_API.get(environment.AWS_Cognito.apiName, url,
      this.request(user, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
    return response?.data?.Data || [];
  }

  private async getQuestions(streamId: any, user: any): Promise<any[]> {
    const response: any = await AWS_API.get(environment.AWS_Cognito.apiName, CONST_AWS_API.GET_QUESTIONS_BY_EVENT_ID + '/' + streamId, this.request(user, {}));
    return response?.data?.Data || [];
  }

  // Same source as EventTicketsComponent's currentLocation (paymentInfo.country).
  private countryName(): string {
    const loc = this.commonSubscribeService.locationInfo;
    const location = loc && Object.keys(loc).length > 0 ? loc : this.storageService.getItem('current_location');
    return location?.country_name || '';
  }

  // Same fallback as EventTicketsComponent.registerEvent() when there is no ?ref in the URL.
  private affiliateRefCode(): string {
    const raw = localStorage.getItem('affiliateReferralCode');
    if (!raw || raw === 'undefined') return '';
    return safeParse(raw) || '';
  }

  private request(user: any, body: any): any {
    return {
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': environment.AWS_Cognito.X_Api_Key,
        Authorization: `Bearer ${user.access_token}`,
      },
      response: true,
      body,
    };
  }
}
